/* Run progress overlay — a non-blocking card centred on the canvas that
   tracks an async generate/prompt run through its five stages. The
   canvas stays visible (and interactive) underneath; only the card
   itself captures pointer events. Elapsed time is driven by the parent
   (EditorView) from run.created_at so a resumed run shows the true clock.

   The inner card is extracted as the exported <RunStepper> so the
   estimate flow can show the same «крутая плашка» inside its modal —
   same classes, same determinate bar, same step list. */

import React, { useEffect, useRef, useState } from 'react';
import { Run, RunStage } from './api';
import { modelDisplay, fmtElapsed } from './utils';

/* ── operator-stop message (DESIGN13 §F3, DESIGN15 §F2.1) ────────
   The backend stores this exact message on a run an operator stopped via the
   cancel route. The UI treats such a run NEUTRALLY (a calm 'Generation
   stopped' toast, no red error banner) rather than as a failure. Exported so
   all three surfaces classify a stopped run the same way — whether they
   stopped it themselves or picked it up as a status-'error' poll from another
   tab. DESIGN15 made this string English + white-label; the matcher stays
   tolerant of the LEGACY Russian text so a run started before the round
   deployed (already in flight) is still recognised, not mislabeled red. */
export const OPERATOR_STOP_ERROR = 'Stopped by the operator.';
const LEGACY_OPERATOR_STOP_ERROR = 'Остановлено оператором.';

/** Did this run end because an operator pressed Stop? Such a run carries the
    fixed stop message above; callers render it neutrally instead of red. Both
    the current English text and the legacy Russian one count. */
export function isOperatorStop(raw: string | null | undefined): boolean {
  const t = (raw || '').trim();
  return t === OPERATOR_STOP_ERROR || t === LEGACY_OPERATOR_STOP_ERROR;
}

/* ── run liveness (DESIGN12 §F2b) ───────────────────────────────
   Track, client-side, the last time the polled run's updated_at CHANGED.
   The comparison is between successive server responses (a string diff) but
   the GAP is timed with the CLIENT clock — so it is skew-free: we never
   subtract a server timestamp from Date.now(). Returns milliseconds since the
   last observed change, or null before the first signal. The backend refreshes
   updated_at on a ~20s heartbeat while inside a long LLM stage, so a healthy
   run keeps resetting this to near-zero; a hung run lets it climb. */
export function useRunLiveness(updatedAt: string | null | undefined): number | null {
  const prevRef = useRef<string | null | undefined>(undefined);
  const [signalAtMs, setSignalAtMs] = useState<number | null>(null);
  const [sinceMs, setSinceMs] = useState<number | null>(null);

  // A new updated_at value = a fresh signal; stamp it with the client clock.
  useEffect(() => {
    if (updatedAt && updatedAt !== prevRef.current) {
      prevRef.current = updatedAt;
      setSignalAtMs(Date.now());
    }
  }, [updatedAt]);

  // Advance 'time since last signal' once a second; resets when a new signal
  // lands (signalAtMs changes) and stops when there is nothing to time yet.
  useEffect(() => {
    if (signalAtMs == null) {
      setSinceMs(null);
      return undefined;
    }
    const upd = () => setSinceMs(Math.max(0, Date.now() - signalAtMs));
    upd();
    const t = window.setInterval(upd, 1000);
    return () => window.clearInterval(t);
  }, [signalAtMs]);

  return sinceMs;
}

// The AI step's copy while the run is on a SECOND validation pass
// (stage 'llm-retry'). A distinct label + amber tint so a normal retry is
// never mistaken for a hang (DESIGN12 §F2a).
const RETRY_LABEL = 'Re-checking & rewriting (attempt 2)';
const RETRY_DESC =
  'Validation flagged something — the AI is fixing it. This is normal.';

// Once the AI step passes this wall-clock mark, show a calm "large documents
// take a while" hint so a long-but-healthy run reads as expected (§F2c).
const LONG_WAIT_MS = 10 * 60 * 1000;
// No fresh signal for this long flips the last-signal line to a warning (§F2b).
const STALE_MS = 60 * 1000;

const STEPS = [
  'Prepare sources',
  'AI is thinking',
  'Validate',
  'Render poster',
  'Save',
];

// A subtle one-line description shown under the current step so the wait
// reads as progress, not a hang.
const STEP_DESC = [
  'Gathering your files and notes for the model',
  'The AI reads your sources and writes the document model',
  'Checking the document model against the schema',
  'Turning the validated model into the poster',
  'Storing the new version',
];

// Determinate progress per step — a monotonically rising fill so the bar
// always moves forward as the run advances through its stages.
const STEP_PCT = [8, 38, 62, 82, 94];

// Map the live backend stage to the active step index. Unknown stages
// fall back to the first step so the card never renders blank. Both the
// poster and the estimate runs share the same stage vocabulary.
const STAGE_STEP: Record<RunStage, number> = {
  preparing: 0,
  llm: 1,
  'llm-retry': 1,
  validating: 2,
  rendering: 3,
  saving: 4,
};

export function stageStep(stage: string): number {
  return STAGE_STEP[stage as RunStage] ?? 0;
}

/* ── the shared stepper card ─────────────────────────────────── */

export interface RunStepperProps {
  /** Step labels top-to-bottom (5 for both surfaces). */
  steps: string[];
  /** Per-step description shown under the current step. */
  stepDesc: string[];
  /** Active step index (0-based). */
  active: number;
  /** Wall-clock elapsed for the current step's timer. */
  elapsedMs: number;
  heading: string;
  /** Already white-labelled tier ('Medium'/'Lite'/…) or null to hide. */
  modelChip?: string | null;
  /** Optional italic excerpt (the prompt instruction). */
  excerpt?: string | null;
  /** Override the current step's description (e.g. a surface-specific line).
      The built-in retry copy takes precedence over this when on 'llm-retry'. */
  currentDesc?: string;
  caption: string;
  /** The live backend stage — drives the distinct retry state (§F2a) and the
      "AI step" gate for the long-wait hint (§F2c). Optional so older callers
      still compile. */
  stage?: string;
  /** The polled run's updated_at — feeds the last-signal liveness line
      (§F2b). Optional; the line is hidden until the first signal is seen. */
  updatedAt?: string | null;
  /** DESIGN13 §F2 — when provided, a quiet ghost «Stop» button appears in the
      header row (right-aligned, never a primary action). Clicking it confirms
      first, then invokes this to cancel the run. Omit to hide the button. */
  onStop?: () => void;
}

export function RunStepper({
  steps,
  stepDesc,
  active,
  elapsedMs,
  heading,
  modelChip,
  excerpt,
  currentDesc,
  caption,
  stage,
  updatedAt,
  onStop,
}: RunStepperProps) {
  const pct = STEP_PCT[active] ?? STEP_PCT[0];
  // A second validation pass (llm-retry) is a DISTINCT visible state, and
  // the same stage marks the long "AI is thinking" phase the hints attach to.
  const retry = stage === 'llm-retry';
  const onAiStep = stage === 'llm' || stage === 'llm-retry';
  const sinceSignalMs = useRunLiveness(updatedAt);
  const stale = sinceSignalMs != null && sinceSignalMs > STALE_MS;
  const longWait = onAiStep && elapsedMs >= LONG_WAIT_MS;
  return (
    <div className="arch-run-card" role="status">
      <div
        className="arch-run-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="arch-run-bar-fill" style={{ width: pct + '%' }} />
      </div>
      <div className="arch-run-head">
        <span className="arch-run-title">{heading}</span>
        <span className="arch-run-head-actions">
          {modelChip ? <span className="arch-run-model">{modelChip}</span> : null}
          {onStop ? (
            <button
              type="button"
              className="arch-btn arch-btn-ghost arch-btn-sm arch-run-stop"
              aria-label="Stop this generation"
              onClick={() => {
                if (window.confirm('Stop this generation?')) onStop();
              }}
            >
              Stop
            </button>
          ) : null}
        </span>
      </div>
      {excerpt ? <div className="arch-run-excerpt">“{excerpt}”</div> : null}

      <ol className="arch-run-steps">
        {steps.map((label, i) => {
          const state = i < active ? 'done' : i === active ? 'current' : 'future';
          const isCurrent = state === 'current';
          // The retry label + amber tint replace the plain AI-step copy on
          // the active step only; the built-in retry desc beats currentDesc.
          const showRetry = isCurrent && retry;
          const displayLabel = showRetry ? RETRY_LABEL : label;
          const desc = showRetry ? RETRY_DESC : currentDesc || stepDesc[i];
          return (
            <li
              key={i}
              className={'arch-run-step ' + state + (showRetry ? ' retry' : '')}
            >
              <span className="arch-run-marker">
                {state === 'done' ? (
                  '✓'
                ) : isCurrent ? (
                  <span className="arch-spinner arch-run-spinner" />
                ) : (
                  <span className="arch-run-dot" />
                )}
              </span>
              <span className="arch-run-step-body">
                <span className="arch-run-step-label">{displayLabel}</span>
                {isCurrent ? (
                  <span className="arch-run-elapsed">{fmtElapsed(elapsedMs)}</span>
                ) : null}
                {isCurrent ? <span className="arch-run-desc">{desc}</span> : null}
                {isCurrent && sinceSignalMs != null ? (
                  <span
                    className={'arch-run-signal' + (stale ? ' stale' : '')}
                    role={stale ? 'alert' : undefined}
                  >
                    {stale
                      ? 'No signal for a while — if this persists the run will be marked lost automatically'
                      : 'Working — last signal ' +
                        Math.round(sinceSignalMs / 1000) +
                        's ago'}
                  </span>
                ) : null}
                {isCurrent && longWait ? (
                  <span className="arch-run-hint">
                    A large document can take 10–15 minutes.
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="arch-run-caption">{caption}</div>
    </div>
  );
}

/* ── poster overlay (canvas-centred) ─────────────────────────── */

interface Props {
  run: Run;
  elapsedMs: number;
  /** DESIGN13 §F2 — forwarded to the stepper's «Stop» button; omit to hide it. */
  onStop?: () => void;
}

export default function RunProgress({ run, elapsedMs, onStop }: Props) {
  const active = stageStep(run.stage);
  const isPrompt = run.kind === 'prompt';

  const heading = isPrompt ? 'Applying your prompt' : 'Generating the poster';
  const excerpt =
    isPrompt && run.instruction
      ? run.instruction.length > 80
        ? run.instruction.slice(0, 80).trimEnd() + '…'
        : run.instruction
      : '';

  return (
    <div className="arch-run-overlay" aria-live="polite">
      <RunStepper
        steps={STEPS}
        stepDesc={STEP_DESC}
        active={active}
        elapsedMs={elapsedMs}
        heading={heading}
        modelChip={run.model ? modelDisplay(run.model) : null}
        excerpt={excerpt}
        stage={run.stage}
        updatedAt={run.updated_at}
        onStop={onStop}
        caption="You can leave this page — generation continues on the server."
      />
    </div>
  );
}
